import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    TableBody as MuiTableBody,
    TableCell,
    TableRow,
    Checkbox,
} from '@material-ui/core';
import { Link } from '@material-ui/core';
import useAuth from 'src/hooks/useAuth';
import SkeletonTable from 'src/components/SkeletonTable';
import { CapitalizeNames } from 'src/utils/capitalize';
import { DEVELOPER_LEVEL_ACCESS } from 'src/components/access';
import { IServiceClient } from 'src/interfaces/serviceClient';
import { IServiceClientsTableBodyProps } from './types';
import { getServiceClientRowDisplayValues } from 'src/modules/serviceClients/helpers/getServiceClientRowDisplayValues';

export const SERVICE_CLIENTS_TABLE_COLUMNS = [
    'name',
    'email',
    'phone',
    'store',
    'assigned',
    'vehicle',
    'createdAt',
];

const VISIBLE_ROWS = {
    name: true,
    email: true,
    phone: true,
    store: true,
    assigned: true,
    vehicle: true,
    createdAt: true,
};

function renderCell(
    key: string,
    row: IServiceClient,
    display: ReturnType<typeof getServiceClientRowDisplayValues>,
    t: (k: string) => string
) {
    const { storeName, agentName, vehicleLabel, createdAtFormatted } = display;
    switch (key) {
        case 'name':
            return (
                <Link
                    color="inherit"
                    component={RouterLink}
                    to={`/management/service-clients/${row._id}`}
                    variant="h6"
                >
                    {row.name ? CapitalizeNames(row.name) : t('Messages.NoneO')}
                </Link>
            );
        case 'email':
            return row.email || t('Messages.NoneO');
        case 'phone':
            return row.phone || t('Messages.NoneO');
        case 'store':
            return storeName ? CapitalizeNames(storeName) : t('Messages.NoneA');
        case 'assigned':
            return agentName ? CapitalizeNames(agentName) : t('Messages.NoneA');
        case 'vehicle':
            return vehicleLabel ?? t('Messages.NoneA');
        case 'createdAt':
            return createdAtFormatted ?? t('Messages.NoneA');
        default:
            return null;
    }
}

export const ServiceClientsTableBody = ({
    clients,
    loading,
    limit,
    visibleRows = VISIBLE_ROWS,
    selectedIds = [],
    onSelectAll,
    onSelectOne,
}: IServiceClientsTableBodyProps) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const visibleColumns = SERVICE_CLIENTS_TABLE_COLUMNS.filter((key) => visibleRows[key]);
    const showCheckboxes =
        Boolean(onSelectOne) && user?.accessLevel === DEVELOPER_LEVEL_ACCESS;

    if (loading) {
        return (
            <MuiTableBody data-testid="service-clients-table-body">
                <SkeletonTable
                    size={limit}
                    rows={visibleColumns}
                    visibleRows={visibleRows}
                    check={showCheckboxes}
                    width={150}
                    height={30}
                />
            </MuiTableBody>
        );
    }

    const rows = clients ?? [];

    return (
        <MuiTableBody data-testid="service-clients-table-body">
            {rows.map((row: IServiceClient) => {
                const display = getServiceClientRowDisplayValues(row);
                const isSelected = selectedIds.includes(row._id);
                return (
                    <TableRow hover key={row._id} selected={isSelected}>
                        {showCheckboxes && (
                            <TableCell padding="checkbox" data-testid={`service-clients-table-row-checkbox-${row._id}`}>
                                <Checkbox
                                    checked={isSelected}
                                    onChange={(e) => onSelectOne?.(e, row._id)}
                                />
                            </TableCell>
                        )}
                        {visibleColumns.map((key) => (
                            <TableCell key={key}>
                                {renderCell(key, row, display, t)}
                            </TableCell>
                        ))}
                    </TableRow>
                );
            })}
        </MuiTableBody>
    );
};
